import { Point2D } from "types/geometry";

export type VideoObjectFitLayout = {
    scale: number;
    offsetX: number;
    offsetY: number;
};

/** Map native video pixels to canvas CSS pixels (object-fit cover/contain). */
export function computeVideoObjectFitLayout(
    canvasWidth: number,
    canvasHeight: number,
    videoWidth: number,
    videoHeight: number,
    fitScreen: boolean,
): VideoObjectFitLayout | null {
    if (!videoWidth || !videoHeight) {
        return null;
    }

    const scale = fitScreen
        ? Math.max(canvasWidth / videoWidth, canvasHeight / videoHeight)
        : Math.min(canvasWidth / videoWidth, canvasHeight / videoHeight);

    return {
        scale,
        offsetX: (canvasWidth - videoWidth * scale) / 2,
        offsetY: (canvasHeight - videoHeight * scale) / 2,
    };
}

export function videoPixelToCanvas(
    u: number,
    v: number,
    layout: VideoObjectFitLayout,
): Point2D {
    return {
        x: u * layout.scale + layout.offsetX,
        y: v * layout.scale + layout.offsetY,
    };
}

/**
 * Map canvas CSS pixels back to native video pixels.
 * Returns null when the point falls in letterbox/pillarbox (outside the fitted video rect).
 */
export function canvasPixelToVideo(
    canvasX: number,
    canvasY: number,
    layout: VideoObjectFitLayout,
    videoWidth: number,
    videoHeight: number,
): Point2D | null {
    if (!layout.scale) return null;

    const u = (canvasX - layout.offsetX) / layout.scale;
    const v = (canvasY - layout.offsetY) / layout.scale;
    if (u < 0 || v < 0 || u > videoWidth || v > videoHeight) {
        return null;
    }
    return { x: u, y: v };
}
